// @ai-generated 2026-08-21T00:00:00Z by niklas.eicker@tngtech.com using Claude Opus 5 (1M context)
import type { EntityPage } from '@utils/toolCallEnvelope';

/** The readers and phrasings `entityTools` is configured with; none of them know about a card. */

export const text =
    (key: string) =>
    (item: Record<string, unknown>): string | undefined =>
        typeof item[key] === 'string' && item[key] !== '' ? (item[key] as string) : undefined;

export const decimal =
    (key: string) =>
    (item: Record<string, unknown>): string | undefined =>
        typeof item[key] === 'number'
            ? item[key].toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : undefined;

export const customer = (item: Record<string, unknown>): string | undefined => {
    const label = text('customerLabel')(item);
    const number = text('customerNumber')(item);
    return [label, number && `(${number})`].filter(Boolean).join(' ') || undefined;
};

/**
 * A search flattens the customer onto its result; the writes return the domain entity, where it is
 * still the nested snapshot. Reading both lets one column definition serve either payload.
 */
export const nestedCustomer = (item: Record<string, unknown>): string | undefined => {
    const snapshot = isRecord(item.customer) ? item.customer : undefined;
    if (snapshot === undefined) {
        return customer(item);
    }

    const label = text('label')(snapshot);
    const number = text('number')(snapshot);
    return [label, number && `(${number})`].filter(Boolean).join(' ') || undefined;
};

/** An enum reaches the client as its member name; a domain payload may send the raw ordinal. */
export const enumText =
    (key: string) =>
    (item: Record<string, unknown>): string | undefined =>
        typeof item[key] === 'string' && item[key] !== ''
            ? (item[key] as string)
            : typeof item[key] === 'number'
              ? String(item[key])
              : undefined;

/** A write states what it changed, and never talks about a total it did not touch. */
export const affected =
    (singular: string, plural: string, verb: string) =>
    (page: EntityPage): string => {
        if (page.items.length === 0) {
            return `Keine ${plural} ${verb}`;
        }
        return page.items.length === 1 ? `1 ${singular} ${verb}` : `${page.items.length} ${plural} ${verb}`;
    };

/** How many records a write was pointed at, while it is still running. */
export const describeIdCount =
    (key: string, singular: string, plural: string) =>
    (args: unknown): string | undefined => {
        if (!isRecord(args)) {
            return undefined;
        }
        if (typeof args[key] === 'string') {
            return `1 ${singular}`;
        }
        const ids = args[key];
        if (!Array.isArray(ids) || ids.length === 0) {
            return undefined;
        }
        return ids.length === 1 ? `1 ${singular}` : `${ids.length} ${plural}`;
    };

/**
 * A search states how many records matched, and says so when the page it returned is only part of
 * them — the agent is told the same, and a reader comparing the table against "alle" needs to know.
 */
export const found =
    (unit: string) =>
    (page: EntityPage): string => {
        if (page.totalItemCount > page.items.length) {
            return `${page.totalItemCount} ${unit} (${page.items.length} angezeigt)`;
        }
        if (page.totalItemCount === 0) {
            return 'Keine Treffer';
        }
        return page.totalItemCount === 1 ? '1 Treffer' : `${page.totalItemCount} ${unit}`;
    };

/** A write states what it created, and never talks about a total it did not produce. */
export const created =
    (singular: string, plural: string) =>
    (page: EntityPage): string => {
        if (page.items.length === 0) {
            return `Keine ${plural} erstellt`;
        }
        return page.items.length === 1 ? `1 ${singular} erstellt` : `${page.items.length} ${plural} erstellt`;
    };

/** What was searched for, from the arguments, while there is no result to show yet. */
export function describeCriteria(args: unknown): string | undefined {
    const query = isRecord(args) && isRecord(args.query) ? args.query : undefined;
    if (query === undefined) {
        return undefined;
    }

    const criteria: string[] = [];
    if (typeof query.fullTextSearch === 'string' && query.fullTextSearch !== '') {
        criteria.push(`„${query.fullTextSearch}“`);
    }
    if (Array.isArray(query.filters)) {
        criteria.push(
            ...query.filters
                .filter(isRecord)
                .map((filter) => `${String(filter.field ?? '?')} = ${String(filter.value ?? '?')}`),
        );
    }
    return criteria.length > 0 ? criteria.join(', ') : undefined;
}

/** How many orders a Faktura run was pointed at, while it is still creating the invoices. */
export function describeOrderCount(args: unknown): string | undefined {
    if (!isRecord(args) || !Array.isArray(args.orderIds) || args.orderIds.length === 0) {
        return undefined;
    }
    return args.orderIds.length === 1 ? 'aus 1 Auftrag' : `aus ${args.orderIds.length} Aufträgen`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}
